/**
 * Message Item Component
 * 
 * Displays a single chat message with sender information, timestamp,
 * read receipts, and support for deleted messages.
 */

import React, { useMemo } from 'react';
import { Avatar, Typography, Tooltip, Button } from 'antd';
import { CheckOutlined, EditOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import DOMPurify from 'dompurify';
import type { Message } from '@municipality/types';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ReactionPicker } from './ReactionPicker';
import { ReplyButton } from './ReplyButton';
import { ReplyPreview } from './ReplyPreview';
import { useResponsive } from '@municipality/ui';

const { Text } = Typography;

/**
 * Get initials from a name (first letter of first and last name)
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  
  // First letter of first name + first letter of last name
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color from a string (name)
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Sanitize message content to prevent XSS attacks
 * Removes all HTML tags and potentially malicious content
 */
const sanitizeMessage = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [],  // No HTML tags allowed - pure text only
    ALLOWED_ATTR: [],  // No attributes allowed
    KEEP_CONTENT: true,  // Keep text content
  });
};

interface MessageItemProps {
  message: Message;
  isOwnMessage: boolean;
  showAvatar?: boolean;
  userId: string;
  participantDetails: Record<string, { name: string; avatar?: string }>;
  onReactionClick: (emoji: string) => void;
  onRemoveReaction?: () => void;
  onEdit?: (message: Message) => void;
  onReply?: (message: Message) => void;
  conversationType?: 'direct' | 'group';
  messages?: Message[];
}

const MessageContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile' && prop !== 'isOwnMessage',
})<{ isOwnMessage: boolean; $isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '12px' : '16px')};
  align-items: flex-start;
  gap: ${({ $isMobile }) => ($isMobile ? '6px' : '8px')};
`;

const MessageBubbleContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
`;

const MessageBubble = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile' && prop !== 'isOwnMessage' && prop !== 'isDeleted',
})<{ isOwnMessage: boolean; isDeleted: boolean; $isMobile: boolean }>`
  display: inline-block;
  padding: ${({ $isMobile }) => ($isMobile ? '10px 12px' : '12px 16px')};
  border-radius: 12px;
  background-color: #f5f5f5;
  color: ${(props) => (props.isDeleted ? '#999' : '#000')};
  font-style: ${(props) => (props.isDeleted ? 'italic' : 'normal')};
  word-break: break-word;
  
  /* Ensure mention highlights maintain their blue color - use very specific selectors */
  span[data-mention="true"],
  span[data-mention="true"] *,
  span[style*="color: #1890ff"],
  span[style*="color:#1890ff"],
  span[style*="color: #1890ff"] *,
  span[style*="color:#1890ff"] * {
    color: #1890ff !important;
  }
`;

const ReactionButtonContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 0;
  gap: 4px;
  align-self: center;
`;

const ReactionEmojiButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$isMobile' && prop !== 'isOwnReaction' && prop !== 'hasMultipleReactions',
})<{ isOwnReaction: boolean; hasMultipleReactions: boolean; $isMobile: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: ${({ $isMobile }) => ($isMobile ? '4px' : '2px')};
  min-width: ${({ $isMobile }) => ($isMobile ? '44px' : 'auto')};
  min-height: ${({ $isMobile }) => ($isMobile ? '44px' : 'auto')};
  transition: transform 0.2s;
  font-size: ${(props) => (props.isOwnReaction && props.hasMultipleReactions ? '20px' : '13px')};
  line-height: 1;
  
  &:hover {
    transform: scale(1.15);
  }
`;

const MessageContent = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  font-size: ${({ $isMobile }) => ($isMobile ? '13px' : '14px')};
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  
  /* Force mention highlights to be blue */
  span[data-mention="true"] {
    color: #1890ff !important;
  }
`;

const MentionHighlight = styled.span`
  color: #1890ff !important;
  font-weight: 500;
  padding: 2px 4px;
  border-radius: 4px;
  display: inline;
  
  &, & * {
    color: #1890ff !important;
  }
`;

const MessageMeta = styled.div<{ isOwnMessage: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  opacity: 0.7;
  color: ${(props) => (props.isOwnMessage ? 'inherit' : '#666')};
  margin-top: 2px;
  white-space: nowrap;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  gap: 8px;
`;

const SenderName = styled(Text)`
  font-weight: 700;
  font-size: 12px;
  opacity: 0.8;
`;

const MessageTimestamp = styled.span`
  font-weight: 400;
  font-size: 11px;
  opacity: 0.6;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ReadStatusContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const ReadStatusIcon = styled(CheckOutlined)`
  font-size: 12px;
  color: #52c41a;
`;

const ReadCountBadge = styled.span`
  font-size: 10px;
  color: #52c41a;
  font-weight: 600;
  margin-left: 2px;
`;

const MessageActions = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isMobile }) => ($isMobile ? '8px' : '4px')};
  margin-left: ${({ $isMobile }) => ($isMobile ? '4px' : '8px')};
  opacity: ${({ $isMobile }) => ($isMobile ? '1' : '0')};
  transition: opacity 0.2s;
`;

const MessageContainerWithHover = styled(MessageContainer, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  ${({ $isMobile }) =>
    !$isMobile &&
    `
    &:hover .message-actions {
      opacity: 1;
    }
  `}
`;

const EditButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  padding: 0;
  width: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  height: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  min-width: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  min-height: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  border: none;
  box-shadow: none;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #1890ff;
    background-color: transparent;
  }
`;

const MessageAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== '$bgColor' && prop !== '$isMobile',
})<{ $bgColor?: string; $isMobile: boolean }>`
  background-color: ${(props) => props.$bgColor || 'transparent'} !important;
  color: #fff !important;
  font-weight: 500 !important;
  font-size: ${({ $isMobile }) => ($isMobile ? '10px' : '13px')} !important;
  flex-shrink: 0;
  width: ${({ $isMobile }) => ($isMobile ? '24px' : '30px')} !important;
  height: ${({ $isMobile }) => ($isMobile ? '24px' : '30px')} !important;
  min-width: ${({ $isMobile }) => ($isMobile ? '24px' : '30px')} !important;
`;

const MessageWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;


export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwnMessage,
  showAvatar = true,
  userId,
  participantDetails,
  onReactionClick,
  onRemoveReaction,
  onEdit,
  onReply,
  conversationType = 'direct',
  messages = [],
}) => {
  const { t } = useTranslation('chat');
  const { isMobile } = useResponsive();
  const isDeleted = message.deletedBy.includes(userId);
  const isSystemMessage = message.type === 'system';
  
  // Calculate thread count (number of replies to this message)
  const threadCount = useMemo(() => {
    if (!messages || messages.length === 0) return 0;
    return messages.filter(m => m.metadata?.replyTo === message.id).length;
  }, [messages, message.id]);
  
  const formatTime = (date: Date) => {
    return format(date, 'HH:mm');
  };

  const initials = getInitials(message.senderName);
  const avatarColor = getAvatarColor(message.senderName);
  
  // Parse mentions and create highlighted content
  const parseMentions = (content: string): React.ReactNode[] => {
    if (!content) return [];
    
    // Sanitize content first
    const sanitized = sanitizeMessage(content);
    
    // Parse mentions for group and direct chats (when metadata has mentions)
    if ((conversationType !== 'group' && conversationType !== 'direct') || !message.metadata?.mentions || message.metadata.mentions.length === 0) {
      return [sanitized];
    }
    
    // Create a map of mentioned user IDs to their display names
    const mentionedUsersMap = new Map<string, string>();
    message.metadata.mentions.forEach((userId) => {
      const participant = participantDetails[userId];
      if (participant?.name) {
        mentionedUsersMap.set(userId, participant.name);
      }
    });
    
    // If no valid mentions found, return sanitized content
    if (mentionedUsersMap.size === 0) {
      return [sanitized];
    }
    
    // Match @username patterns - match @ followed by name (word chars and spaces) until space or end
    // Pattern matches: @name or @John Doe (but stops at space before next word)
    // Updated pattern to match 1-2 words (most names are first name + last name)
    // The pattern: @([^\s@]+(?:\s+[^\s@]+)?)(?=\s|$) matches:
    // - @name (single word, stops at space or end)
    // - @John Doe (two words, stops at space before next word like "welcome")
    // The (?=\s|$) is a positive lookahead that ensures we stop at space or end of string
    const mentionPattern = /@([^\s@]+(?:\s+[^\s@]+)?)(?=\s|$)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let mentionIndex = 0; // Track which mention we're processing
    
    // Reset regex lastIndex
    mentionPattern.lastIndex = 0;
    
    while ((match = mentionPattern.exec(sanitized)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        const textBefore = sanitized.substring(lastIndex, match.index);
        parts.push(textBefore);
      }
      
      const mentionText = match[0]; // Full match including @
      const mentionName = match[1].trim(); // Name without @, trimmed
      
      // Find the mentioned user ID by matching the name
      let mentionedUserId: string | undefined;
      let displayName: string | undefined;
      
      // Try to match by checking if mention name matches any mentioned user's name
      for (const [userId, name] of mentionedUsersMap.entries()) {
        const nameLower = name.toLowerCase().trim();
        const mentionNameLower = mentionName.toLowerCase().trim();
        
        // Exact match (case-insensitive)
        if (nameLower === mentionNameLower) {
          mentionedUserId = userId;
          displayName = name;
          break;
        }
        
        // Match if mention name is the start of the full name (e.g., "John" matches "John Doe")
        if (nameLower.startsWith(mentionNameLower + ' ')) {
          mentionedUserId = userId;
          displayName = name;
          break;
        }
        
        // Match if the first word(s) of the full name match (e.g., "John" matches "John Michael Doe")
        const nameWords = nameLower.split(' ');
        const mentionWords = mentionNameLower.split(' ');
        if (mentionWords.length <= nameWords.length) {
          const nameStart = nameWords.slice(0, mentionWords.length).join(' ');
          if (nameStart === mentionNameLower) {
            mentionedUserId = userId;
            displayName = name;
            break;
          }
        }
      }
      
      // If found, display the full name in blue
      if (mentionedUserId && displayName) {
        parts.push(
          <MentionHighlight 
            key={`mention-${match.index}-${mentionIndex}`}
            style={{ color: '#1890ff', fontWeight: 500 }}
            data-mention="true"
          >
            @{displayName}
          </MentionHighlight>
        );
        mentionIndex++;
      } else {
        // Not a valid mention, just show as text
        parts.push(mentionText);
      }
      
      // Update lastIndex to the end of the current match
      // Use match.index + match[0].length instead of mentionPattern.lastIndex
      // to ensure we correctly handle the position even when there's text after the mention
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < sanitized.length) {
      const remainingText = sanitized.substring(lastIndex);
      parts.push(remainingText);
    }
    
    return parts.length > 0 ? parts : [sanitized];
  };
  
  // Sanitize message content for XSS protection
  const sanitizedContent = isDeleted 
    ? t('messages.deleted')
    : sanitizeMessage(message.content);
  
  // Parse mentions for display
  const contentWithMentions = isDeleted 
    ? sanitizedContent
    : parseMentions(message.content);
  
  // Get read receipt info for group chats (skip for system messages)
  const getReadReceiptInfo = (): { text: string; isRead: boolean; count: number } | null => {
    if (!isOwnMessage || isDeleted || isSystemMessage) return null;
    
    const readByOthers = Object.keys(message.readBy).filter(id => id !== message.senderId);
    const readCount = readByOthers.length;
    
    if (readCount === 0) {
      return { text: t('readReceipts.sent'), isRead: false, count: 0 };
    }
    
    // For group chats, show read count and names
    if (conversationType === 'group') {
      const readerNames = readByOthers
        .slice(0, 3)
        .map(id => participantDetails[id]?.name || 'Unknown')
        .filter(Boolean);
      
      if (readCount <= 3) {
        return {
          text: t('readReceipts.readByMultiple', {
            names: readerNames.join(', '),
            count: 0
          }).replace(' and 0 more', ''),
          isRead: true,
          count: readCount
        };
      } else {
        return {
          text: t('readReceipts.readByMultiple', {
            names: readerNames.join(', '),
            count: readCount - 3
          }),
          isRead: true,
          count: readCount
        };
      }
    }
    
    // For direct chats, just show "Read"
    return { text: t('readReceipts.read'), isRead: true, count: readCount };
  };
  
  const readReceiptInfo = getReadReceiptInfo();

  // System messages: centered, italic, no avatar, no reactions, no read receipts
  if (isSystemMessage) {
    // Translate system messages if they match known patterns
    let displayContent = sanitizedContent;
    
    // Check if it's a "left the group chat" message and translate it
    if (sanitizedContent.includes(' left the group chat')) {
      const nameMatch = sanitizedContent.match(/^(.+?) left the group chat$/);
      if (nameMatch) {
        const name = nameMatch[1];
        displayContent = t('systemMessages.leftGroup', { name });
      }
    }
    
    return (
      <MessageContainerWithHover id={`message-${message.id}`} isOwnMessage={false} $isMobile={isMobile} style={{ justifyContent: 'center', marginBottom: isMobile ? '6px' : '8px' }}>
        <MessageBubble isOwnMessage={false} isDeleted={false} $isMobile={isMobile} style={{ 
          background: 'transparent', 
          padding: '4px 8px',
          fontStyle: 'italic',
          color: '#999',
          fontSize: '12px'
        }}>
          <MessageContent $isMobile={isMobile}>{displayContent}</MessageContent>
        </MessageBubble>
      </MessageContainerWithHover>
    );
  }

  return (
    <MessageContainerWithHover id={`message-${message.id}`} isOwnMessage={isOwnMessage} $isMobile={isMobile}>
      {showAvatar && (
        <MessageAvatar
          src={message.senderAvatar}
          $bgColor={message.senderAvatar ? undefined : avatarColor}
          $isMobile={isMobile}
        >
          {!message.senderAvatar && initials}
        </MessageAvatar>
      )}
      
      <MessageWrapper>
        {/* Reply Preview */}
        {message.metadata?.replyTo && messages.length > 0 && (() => {
          const repliedToMessage = messages.find(m => m.id === message.metadata?.replyTo);
          if (repliedToMessage) {
            return (
              <div style={{ marginBottom: 8 }}>
                <ReplyPreview
                  repliedToMessage={repliedToMessage}
                  participantDetails={participantDetails}
                  onClick={() => {
                    // Scroll to replied message
                    const event = new globalThis.CustomEvent('jumpToMessage', {
                      detail: { messageId: repliedToMessage.id },
                    });
                    window.dispatchEvent(event);
                  }}
                />
              </div>
            );
          }
          // If replied-to message not found, show a placeholder
          return (
            <div style={{ marginBottom: 8 }}>
              <ReplyPreview
                repliedToMessage={{
                  ...message,
                  id: message.metadata!.replyTo!,
                  content: t('threading.messageNotFound') || 'Original message not found',
                  senderName: 'Unknown',
                  deletedBy: [message.metadata!.replyTo!], // Mark as deleted to show placeholder
                } as Message}
                participantDetails={participantDetails}
              />
            </div>
          );
        })()}
        
        <MessageHeader>
          <SenderName>{message.senderName}</SenderName>
          <MessageTimestamp>
            {formatTime(message.createdAt)}
            {isOwnMessage && !isDeleted && readReceiptInfo && (
              <Tooltip title={readReceiptInfo.text}>
                <ReadStatusContainer>
                  {/* Show 2 checks if message has been read, otherwise 1 check for sent */}
                  {readReceiptInfo.isRead ? (
                    <>
                      <ReadStatusIcon />
                      <ReadStatusIcon />
                      {conversationType === 'group' && readReceiptInfo.count > 0 && (
                        <ReadCountBadge>{readReceiptInfo.count}</ReadCountBadge>
                      )}
                    </>
                  ) : (
                    <ReadStatusIcon />
                  )}
                </ReadStatusContainer>
              </Tooltip>
            )}
          </MessageTimestamp>
          {!isDeleted && !isSystemMessage && (
            <MessageActions className="message-actions" $isMobile={isMobile}>
              {isOwnMessage && onEdit && (
                <Tooltip title={t('editMessage.button') || 'Edit message'}>
                  <EditButton
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => onEdit(message)}
                    $isMobile={isMobile}
                  />
                </Tooltip>
              )}
              {onReply && !isSystemMessage && (
                <ReplyButton onClick={() => onReply(message)} />
              )}
            </MessageActions>
          )}
        </MessageHeader>
        
        <MessageBubbleContainer>
          <MessageBubble isOwnMessage={isOwnMessage} isDeleted={isDeleted} $isMobile={isMobile}>
            <MessageContent $isMobile={isMobile}>
              {typeof contentWithMentions === 'string' 
                ? contentWithMentions 
                : contentWithMentions}
            </MessageContent>
            
            <MessageMeta isOwnMessage={isOwnMessage}>
              {message.isEdited && !isDeleted && <span>• {t('messages.edited')}</span>}
              {threadCount > 0 && !isDeleted && (
                <span style={{ marginLeft: message.isEdited ? 8 : 0, color: '#1890ff', cursor: 'pointer' }}>
                  {t('threading.replyCount', { count: threadCount })}
                </span>
              )}
            </MessageMeta>
          </MessageBubble>
          
          {!isDeleted && (
            <ReactionButtonContainer>
              {message.reactions && Object.keys(message.reactions).length > 0 ? (
                // Show reactions if they exist - wrap in Popover for clicking
                <ReactionPicker
                  onSelectReaction={onReactionClick}
                  onRemoveReaction={onRemoveReaction}
                  currentReaction={message.reactions[userId]}
                >
                  {Object.entries(message.reactions).map(([reactionUserId, emoji]) => {
                    const isOwnReaction = reactionUserId === userId;
                    const hasMultipleReactions = Object.keys(message.reactions || {}).length > 1;
                    
                    return (
                      <ReactionEmojiButton
                        key={reactionUserId}
                        isOwnReaction={isOwnReaction}
                        hasMultipleReactions={hasMultipleReactions}
                        $isMobile={isMobile}
                        title={participantDetails[reactionUserId]?.name || 'Unknown'}
                      >
                        {emoji}
                      </ReactionEmojiButton>
                    );
                  })}
                </ReactionPicker>
              ) : (
                // Show heart icon only when no reactions exist
                <ReactionPicker
                  onSelectReaction={onReactionClick}
                  onRemoveReaction={onRemoveReaction}
                  currentReaction={undefined}
                />
              )}
            </ReactionButtonContainer>
          )}
        </MessageBubbleContainer>
      </MessageWrapper>
    </MessageContainerWithHover>
  );
};

